import { supabase } from '../lib/supabase';
import { messageQueue, MESSAGE_STATUS } from '../utils/messageQueue';

/**
 * REBUILT Messaging Service - Fixed All Issues
 * - Proper RPC function usage
 * - Optimized queries
 * - Fixed real-time subscriptions
 * - Better error handling
 */
export const messagingService = {
  // Export supabase for external use if needed
  supabase,
  /**
   * Get all conversations for the current user
   */
  async getMyConversations() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('Mesajları görmek için giriş yapmalısınız.');
      }

      // Use RPC function for optimized query
      const { data, error } = await supabase?.rpc('get_user_conversations_rpc');

      if (error) {
        throw new Error(`Sohbetler yüklenirken hata: ${error.message}`);
      }

      if (!data || data?.length === 0) {
        return [];
      }

      // Map RPC response to UI structure
      const conversations = data?.map(c => ({
        conversationId: c?.conversation_id,
        type: 'direct',
        userId: c?.partner_id,
        userName: c?.partner_name || 'Bilinmeyen Kullanıcı',
        userAvatar: c?.partner_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(c?.partner_name || 'User')}&background=0D8ABC&color=fff`,
        lastMessage: c?.last_message || 'Henüz mesaj yok',
        lastMessageTime: c?.last_message_time || c?.updated_at,
        unreadCount: parseInt(c?.unread_count) || 0
      }));

      return conversations;

    } catch (error) {
      throw error;
    }
  },

  /**
   * Get or create a direct conversation with another user
   */
  async getOrCreateDirectConversation(otherUserId) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('Mesaj göndermek için giriş yapmalısınız.');
      }

      if (!otherUserId) {
        throw new Error('Alıcı kullanıcı ID\'si gereklidir.');
      }

      // Call database function
      const { data: conversationId, error } = await supabase?.rpc('get_or_create_direct_conversation', {
        other_user_id: otherUserId
      });

      if (error) {
        throw new Error(`Sohbet oluşturulurken hata: ${error.message}`);
      }

      if (!conversationId) {
        throw new Error('Sohbet ID\'si alınamadı.');
      }

      return conversationId;

    } catch (error) {
      throw error;
    }
  },

  /**
   * Get messages in a specific conversation
   */
  async getConversationMessages(conversationId) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('Mesajları görmek için giriş yapmalısınız.');
      }

      if (!conversationId) {
        throw new Error('Sohbet ID\'si gereklidir.');
      }

      // Fixed: Use correct Supabase foreign key join syntax
      const { data: messages, error } = await supabase?.from('conversation_messages')?.select(`
          id,
          content,
          message_type,
          is_read,
          created_at,
          sender_id,
          user_profiles!sender_id (
            id,
            full_name,
            avatar_url,
            email
          )
        `)?.eq('conversation_id', conversationId)?.order('created_at', { ascending: true });

      if (error) {
        throw new Error(`Mesajlar yüklenirken hata: ${error.message}`);
      }

      const formattedMessages = (messages || [])?.map(msg => ({
        id: msg?.id,
        senderId: msg?.sender_id,
        content: msg?.content,
        messageType: msg?.message_type,
        isRead: msg?.is_read,
        createdAt: msg?.created_at,
        sender: {
          id: msg?.user_profiles?.id,
          name: msg?.user_profiles?.full_name || msg?.user_profiles?.email || 'İsimsiz Kullanıcı',
          avatar: msg?.user_profiles?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(msg?.user_profiles?.full_name || 'User')}&background=0D8ABC&color=fff`,
          email: msg?.user_profiles?.email
        }
      }));

      return formattedMessages;

    } catch (error) {
      throw error;
    }
  },

  /**
   * Send a message in a conversation
   */
  async sendMessage(conversationId, content, options = {}) {
    const { skipQueue = false, tempId = null } = options;

    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('Mesaj göndermek için giriş yapmalısınız.');
      }

      if (!conversationId) {
        throw new Error('Sohbet ID\'si gereklidir.');
      }

      if (!content || !content?.trim()) {
        throw new Error('Mesaj içeriği boş olamaz.');
      }

      // Queue if offline
      if (!navigator.onLine && !skipQueue) {
        const queueId = messageQueue?.enqueue({
          conversationId,
          content: content?.trim(),
          messageType: 'text',
          tempId
        });

        return {
          id: queueId,
          tempId,
          senderId: user?.id,
          content: content?.trim(),
          messageType: 'text',
          isRead: false,
          createdAt: new Date()?.toISOString(),
          status: MESSAGE_STATUS?.PENDING,
          isQueued: true,
          sender: {
            id: user?.id,
            name: 'You',
            avatar: user?.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=You&background=0D8ABC&color=fff`,
            email: user?.email
          }
        };
      }

      // Send message
      const { data, error } = await supabase?.from('conversation_messages')?.insert({
          conversation_id: conversationId,
          sender_id: user?.id,
          content: content?.trim(),
          message_type: 'text'
        })?.select(`
          id,
          content,
          message_type,
          is_read,
          created_at,
          sender_id,
          sender:sender_id (
            id,
            full_name,
            avatar_url,
            email
          )
        `)?.single();

      if (error) {
        
        // Queue on error if not already from queue
        if (!skipQueue) {
          const queueId = messageQueue?.enqueue({
            conversationId,
            content: content?.trim(),
            messageType: 'text',
            tempId
          });

          return {
            id: queueId,
            tempId,
            senderId: user?.id,
            content: content?.trim(),
            messageType: 'text',
            isRead: false,
            createdAt: new Date()?.toISOString(),
            status: MESSAGE_STATUS?.PENDING,
            isQueued: true,
            sender: {
              id: user?.id,
              name: 'You',
              avatar: user?.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=You&background=0D8ABC&color=fff`,
              email: user?.email
            }
          };
        }
        
        throw error;
      }

      return {
        id: data?.id,
        senderId: data?.sender_id,
        content: data?.content,
        messageType: data?.message_type,
        isRead: data?.is_read,
        createdAt: data?.created_at,
        status: MESSAGE_STATUS?.SENT,
        isQueued: false,
        sender: {
          id: data?.sender?.id,
          name: data?.sender?.full_name || data?.sender?.email || 'İsimsiz Kullanıcı',
          avatar: data?.sender?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(data?.sender?.full_name || 'User')}&background=0D8ABC&color=fff`,
          email: data?.sender?.email
        }
      };

    } catch (error) {
      
      // Queue on error if not from queue
      if (!skipQueue) {
        const { data: { user } } = await supabase?.auth?.getUser();
        
        const queueId = messageQueue?.enqueue({
          conversationId,
          content: content?.trim(),
          messageType: 'text',
          tempId: options?.tempId
        });

        return {
          id: queueId,
          tempId: options?.tempId,
          senderId: user?.id,
          content: content?.trim(),
          messageType: 'text',
          isRead: false,
          createdAt: new Date()?.toISOString(),
          status: MESSAGE_STATUS?.PENDING,
          isQueued: true,
          sender: {
            id: user?.id,
            name: 'You',
            avatar: user?.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=You&background=0D8ABC&color=fff`,
            email: user?.email
          }
        };
      }
      
      throw error;
    }
  },

  /**
   * Send a direct message to a specific user
   */
  async sendDirectMessage(receiverId, content, options = {}) {
    try {
      if (!receiverId) {
        throw new Error('Alıcı kullanıcı ID\'si gereklidir.');
      }

      if (!content || !content?.trim()) {
        throw new Error('Mesaj içeriği boş olamaz.');
      }

      // Get or create conversation
      const conversationId = await this.getOrCreateDirectConversation(receiverId);

      // Send message
      const sentMessage = await this.sendMessage(conversationId, content, options);

      return sentMessage;

    } catch (error) {
      throw error;
    }
  },

  /**
   * Mark conversation as read
   */
  async markConversationAsRead(conversationId) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        console.warn('⚠️ User not authenticated');
        return false;
      }

      if (!conversationId) {
        console.warn('⚠️ No conversation ID provided');
        return false;
      }

      const { error } = await supabase?.from('conversation_participants')?.update({ last_read_at: new Date()?.toISOString() })?.eq('conversation_id', conversationId)?.eq('user_id', user?.id);

      if (error) {
        console.error('❌ Error marking conversation as read:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('❌ Error marking conversation as read:', {
        message: error?.message || 'Unknown error',
        details: error?.toString() || '',
        hint: error?.hint || '',
        code: error?.code || ''
      });
      return false;
    }
  },

  /**
   * Subscribe to new messages in a conversation
   */
  subscribeToConversation(conversationId, callback) {
    if (!conversationId || !callback) {
      console.warn('⚠️ Invalid subscription parameters');
      return null;
    }

    try {
      const channelName = `conversation-${conversationId}`;
      console.log(`🔔 Subscribing to conversation: ${channelName}`);

      const channel = supabase?.channel(channelName)?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'conversation_messages',
            filter: `conversation_id=eq.${conversationId}`
          },
          async (payload) => {
            try {
              const newMessage = payload?.new;
              console.log('📨 New message received:', newMessage?.id);

              // Fetch full message with sender info
              const { data: fullMessage, error } = await supabase?.from('conversation_messages')?.select(`
                  id,
                  content,
                  message_type,
                  is_read,
                  created_at,
                  sender_id,
                  sender:sender_id (
                    id,
                    full_name,
                    avatar_url,
                    email
                  )
                `)?.eq('id', newMessage?.id)?.single();

              if (error) {
                console.error('❌ Error fetching message details:', error);
                return;
              }

              if (fullMessage) {
                callback({
                  id: fullMessage?.id,
                  senderId: fullMessage?.sender_id,
                  content: fullMessage?.content,
                  messageType: fullMessage?.message_type,
                  isRead: fullMessage?.is_read,
                  createdAt: fullMessage?.created_at,
                  sender: {
                    id: fullMessage?.sender?.id,
                    name: fullMessage?.sender?.full_name || fullMessage?.sender?.email || 'İsimsiz Kullanıcı',
                    avatar: fullMessage?.sender?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(fullMessage?.sender?.full_name || 'User')}&background=0D8ABC&color=fff`,
                    email: fullMessage?.sender?.email
                  }
                });
              }
            } catch (error) {
              console.error('❌ Error processing real-time message:', error);
            }
          }
        )?.subscribe((status) => {
          console.log(`🔔 Subscription status for ${channelName}:`, status);
        });

      return channel;

    } catch (error) {
      console.error('❌ Error setting up subscription:', error);
      return null;
    }
  },

  /**
   * Unsubscribe from channel
   */
  async unsubscribe(channel) {
    if (!channel) return;

    try {
      await supabase?.removeChannel(channel);
      console.log('✅ Unsubscribed from channel');
    } catch (error) {
      console.error('❌ Error unsubscribing:', error);
    }
  },

  /**
   * Subscribe to notifications
   */
  subscribeToNotifications(callback) {
    if (!callback) {
      console.warn('⚠️ No callback provided');
      return null;
    }

    try {
      const channelName = `notifications-realtime`;
      console.log(`🔔 Subscribing to notifications: ${channelName}`);

      // Remove any existing channel with the same name to avoid duplicate subscription errors
      const existingChannel = supabase?.getChannels()?.find(ch => ch?.topic === `realtime:${channelName}`);
      if (existingChannel) {
        supabase?.removeChannel(existingChannel);
      }

      const channel = supabase?.channel(channelName)?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications'
          },
          async (payload) => {
            try {
              const newNotif = payload?.new;

              // Check current user - only notify if this notification belongs to them
              const { data: { user: currentUser } } = await supabase?.auth?.getUser();
              if (!currentUser || newNotif?.user_id !== currentUser?.id) {
                return;
              }

              console.log('🔔 New notification:', newNotif?.id);

              callback({
                id: newNotif?.id,
                title: newNotif?.title,
                message: newNotif?.message,
                type: newNotif?.type || 'system',
                read: newNotif?.read || false,
                actionUrl: newNotif?.action_url,
                timestamp: new Date(newNotif?.created_at),
                icon: getNotificationIcon(newNotif?.type)
              });

            } catch (error) {
              console.error('❌ Error processing notification:', error);
            }
          }
        )?.subscribe((status) => {
          console.log(`🔔 Notification subscription status:`, status);
        });

      return channel;

    } catch (error) {
      console.error('❌ Error setting up notifications:', error);
      return null;
    }
  },

  /**
   * Subscribe to new messages for a specific user (for notification badge)
   */
  subscribeToNewMessages: function(userId, callback) {
    if (!userId || !callback) {
      console.warn('⚠️ Invalid subscription parameters');
      return null;
    }

    try {
      const channelName = `user-messages-${userId}`;
      console.log(`🔔 Subscribing to new messages for user: ${userId}`);

      // Remove any existing channel with the same name to avoid duplicate subscription errors
      const existingChannel = supabase?.getChannels()?.find(ch => ch?.topic === `realtime:${channelName}`);
      if (existingChannel) {
        supabase?.removeChannel(existingChannel);
      }

      const channel = supabase?.channel(channelName)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'conversation_messages',
          filter: `receiver_id=eq.${userId}`
        },
        async (payload) => {
          try {
            const newMessage = payload?.new;
            console.log('📬 New message received:', newMessage?.id);

            // Call the callback with message data
            callback({
              id: newMessage?.id,
              senderId: newMessage?.sender_id,
              conversationId: newMessage?.conversation_id,
              content: newMessage?.content,
              timestamp: newMessage?.created_at
            });
          } catch (error) {
            console.error('❌ Error processing new message:', error);
          }
        }
      )?.subscribe((status) => {
        console.log(`🔔 User messages subscription status:`, status);
      });

      // Return subscription object with unsubscribe method
      return {
        unsubscribe: async () => {
          try {
            await supabase?.removeChannel(channel);
            console.log('✅ Unsubscribed from user messages');
          } catch (error) {
            console.error('❌ Error unsubscribing:', error);
          }
        }
      };

    } catch (error) {
      console.error('❌ Error setting up new messages subscription:', error);
      return null;
    }
  },

  /**
   * Edit a message
   */
  async editMessage(messageId, newContent) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('Mesajı düzenlemek için giriş yapmalısınız.');
      }

      if (!messageId || !newContent || !newContent?.trim()) {
        throw new Error('Geçersiz parametreler.');
      }

      const { data, error } = await supabase?.from('conversation_messages')?.update({
          content: newContent?.trim(),
          updated_at: new Date()?.toISOString()
        })?.eq('id', messageId)?.eq('sender_id', user?.id)?.select(`
          id,
          content,
          message_type,
          is_read,
          created_at,
          updated_at,
          sender_id,
          sender:sender_id (
            id,
            full_name,
            avatar_url,
            email
          )
        `)?.single();

      if (error) {
        throw new Error(`Mesaj düzenlenirken hata: ${error.message}`);
      }

      return {
        id: data?.id,
        senderId: data?.sender_id,
        content: data?.content,
        messageType: data?.message_type,
        isRead: data?.is_read,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        isEdited: true,
        sender: {
          id: data?.sender?.id,
          name: data?.sender?.full_name || data?.sender?.email || 'İsimsiz Kullanıcı',
          avatar: data?.sender?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(data?.sender?.full_name || 'User')}&background=0D8ABC&color=fff`,
          email: data?.sender?.email
        }
      };

    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete a message
   */
  async deleteMessage(messageId) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('Mesajı silmek için giriş yapmalısınız.');
      }

      if (!messageId) {
        throw new Error('Mesaj ID\'si gereklidir.');
      }

      const { error } = await supabase?.from('conversation_messages')?.delete()?.eq('id', messageId)?.eq('sender_id', user?.id);

      if (error) {
        throw new Error(`Mesaj silinirken hata: ${error.message}`);
      }

      return true;

    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete a conversation
   */
  async deleteConversation(conversationId) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('Sohbeti silmek için giriş yapmalısınız.');
      }

      if (!conversationId) {
        throw new Error('Sohbet ID\'si gereklidir.');
      }

      const { error } = await supabase?.from('conversations')?.delete()?.eq('id', conversationId)?.eq('created_by', user?.id);

      if (error) {
        throw new Error(`Sohbet silinirken hata: ${error.message}`);
      }

      return true;

    } catch (error) {
      throw error;
    }
  },

  // Queue management methods
  getQueueStatus() {
    return messageQueue?.getQueueStatus();
  },

  getFailedMessages() {
    return messageQueue?.getFailedMessages();
  },

  retryFailedMessage(queueMessageId) {
    messageQueue?.retryMessage(queueMessageId);
  },

  onMessageStatusUpdate(queueMessageId, callback) {
    messageQueue?.onStatusUpdate(queueMessageId, callback);
  },

  offMessageStatusUpdate(queueMessageId) {
    messageQueue?.offStatusUpdate(queueMessageId);
  },

  /**
   * Get total unread message count across all conversations
   */
  async getTotalUnreadCount() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        return 0;
      }

      // Use RPC function to get conversations with unread counts
      const { data, error } = await supabase?.rpc('get_user_conversations_rpc');

      if (error) {
        return 0;
      }

      if (!data || data?.length === 0) {
        return 0;
      }

      // Sum up all unread counts
      const totalUnread = data?.reduce((sum, conversation) => {
        return sum + (parseInt(conversation?.unread_count) || 0);
      }, 0);

      return totalUnread;

    } catch (error) {
      // Silently return 0 on network errors (e.g. Supabase unreachable)
      return 0;
    }
  },

  /**
   * Get user notifications for UI NotificationCenter
   * Fetches from the notifications table
   */
  async getUserNotifications() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        console.warn('⚠️ User not authenticated');
        return [];
      }

      const { data, error } = await supabase
        ?.from('notifications')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(50);

      if (error) {
        console.error('❌ Error fetching notifications:', error);
        return [];
      }

      const notifications = (data || [])?.map(n => ({
        id: n?.id,
        title: n?.title,
        message: n?.message,
        type: n?.type || 'system',
        read: n?.read || false,
        actionUrl: n?.action_url,
        timestamp: new Date(n?.created_at),
        icon: getNotificationIcon(n?.type),
        priority: n?.type === 'waitlist' ? 'high' : 'normal'
      }));

      return notifications;

    } catch (error) {
      console.error('❌ Error in getUserNotifications:', error);
      return [];
    }
  },

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        console.warn('⚠️ User not authenticated');
        return false;
      }

      const { error } = await supabase
        ?.from('notifications')
        ?.update({ read: true })
        ?.eq('id', notificationId)
        ?.eq('user_id', user?.id);

      if (error) {
        console.error('❌ Error marking notification as read:', error);
        return false;
      }

      return true;

    } catch (error) {
      console.error('❌ Error in markAsRead:', error);
      return false;
    }
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        console.warn('⚠️ User not authenticated');
        return false;
      }

      const { error } = await supabase
        ?.from('notifications')
        ?.update({ read: true })
        ?.eq('user_id', user?.id)
        ?.eq('read', false);

      if (error) {
        console.error('❌ Error marking all notifications as read:', error);
        return false;
      }

      return true;

    } catch (error) {
      console.error('❌ Error in markAllAsRead:', error);
      return false;
    }
  },

  /**
   * Delete all notifications
   */
  async deleteAllNotifications() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        console.warn('⚠️ User not authenticated');
        return false;
      }

      const { error } = await supabase
        ?.from('notifications')
        ?.delete()
        ?.eq('user_id', user?.id);

      if (error) {
        console.error('❌ Error deleting all notifications:', error);
        return false;
      }

      return true;

    } catch (error) {
      console.error('❌ Error in deleteAllNotifications:', error);
      return false;
    }
  },

  /**
   * Delete a specific notification
   */
  async deleteNotification(notificationId) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        console.warn('⚠️ User not authenticated');
        return false;
      }

      const { error } = await supabase
        ?.from('notifications')
        ?.delete()
        ?.eq('id', notificationId)
        ?.eq('user_id', user?.id);

      if (error) {
        console.error('❌ Error deleting notification:', error);
        return false;
      }

      return true;

    } catch (error) {
      console.error('❌ Error in deleteNotification:', error);
      return false;
    }
  }
};

function getNotificationIcon(type) {
  const icons = {
    weather: 'CloudRain',
    waitlist: 'UserPlus',
    message: 'MessageCircle',
    booking: 'Calendar',
    system: 'Settings',
    info: 'Info',
    order: 'ShoppingBag',
    event: 'Calendar',
    cancellation: 'XCircle',
    badge: 'Award'
  };
  return icons?.[type] || 'Bell';
}